import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import debounce from 'lodash/debounce';

import { Grid, Card } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import TableTypography from 'components/TableTypography/TableTypography';

import DataTable from 'examples/Tables/DataTable';

import {
  getSellProperties,
  resetSellPropertiesList,
} from 'store/reducers/propertiesSlice';

import { formatPhoneNumber } from 'helpers/formatData';
import { sellYourPropertiesColumns } from 'constants/columns.constants';

const SellProperty = () => {
  const dispatch = useDispatch();
  const { sellPropertiesList } = useSelector((state) => state.properties);

  const [isLoading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    id: 'id',
    desc: true,
  });
  const [searchTerm, setSearchTerm] = useState('');

  const columns = useMemo(() => {
    return sellYourPropertiesColumns;
  }, []);

  const rows = useMemo(() => {
    return sellPropertiesList?.sellPropertiesData?.length > 0
      ? sellPropertiesList?.sellPropertiesData?.map((property) => ({
          id: property?.id ?? 'NA',
          firstName: `${property?.firstName} ${property?.lastName}`,
          email: property?.email ?? 'NA',
          phoneNumber: formatPhoneNumber(property?.phoneNumber) ?? 'NA',
          propertyAddress: (
            <TableTypography value={property?.propertyAddress ?? 'NA'} />
          ),
          propertyType: property?.propertyType ?? 'NA',
          askingPrice: property?.askingPrice ?? 'NA',
          duration: property?.duration ?? 'NA',
          isOccupied: property?.isOccupied ? 'Yes' : 'No',
        }))
      : [];
  }, [sellPropertiesList?.sellPropertiesData]);

  async function fetchSellPropertiesList() {
    try {
      setLoading(true);
      await dispatch(
        getSellProperties({
          page: currentPage + 1,
          limit: perPage,
          sortField: sortBy?.id ?? 'id',
          sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
          search: searchTerm,
        })
      );
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  const onFetchDataDebounced = debounce(fetchSellPropertiesList, 0);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, sortBy, searchTerm, perPage]);

  useEffect(() => {
    return () => {
      dispatch(resetSellPropertiesList());
    };
  }, []);

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Sell Your Property
              </MDTypography>
            </MDBox>
            <MDBox
              sx={{
                position: 'relative',
                paddingTop: '85px',
                paddingBottom: {
                  xs: '150px',
                  sm: '95px',
                },
              }}
            >
              <DataTable
                table={{ columns, rows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: perPage }}
                showTotalEntries
                canSearch={false}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                perPage={perPage}
                setPerPage={setPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPage={sellPropertiesList?.totalPage}
                totalRecords={sellPropertiesList?.totalCount}
                hiddenColumns={['id']}
                queryPageSortBy={[sortBy]}
                setSortBy={setSortBy}
                isLoading={isLoading}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default SellProperty;
